import {
  CAR_ABOUT_STEP,
  CAR_FIND_STEP,
  CAR_PRICE_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car";
import {
  CarOwner,
  CarProductDetailsFormValues,
} from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductDetails/FormElements/interfaces";

import { CarProductIntroFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductIntro/FormElements/interfaces";
import { CarPriceFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/interfaces";
import { usedByYoungerDriver } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/utils/usedByYoungerDriver";
import { WarrantyType } from "Store/findVehicle/interfaces";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { Trafikkforsikringsavgift } from "Store/interfaces/Trafikkforsikringsavgift";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  AutoRentalTypeVariable,
  RENTAL_TYPE_VARIABLE,
} from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseCarProduct";
import { mapFormToInjuries } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { getDateStringFromAge } from "Utils/dateUtils";
import { formBooleanToBoolean } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { isSTBEmployee } from "Utils/isSTBEmployee";

export type CarProductBackend = {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: any;
  licensePlateNumber: string | null;
  brand?: string;
  model?: string;
  type?: string;
  modelYear?: number;
  kw: number;
  volume: number;
  licensePlateNumberProvided?: boolean;
  insuranceHolderIsOwner: boolean;
  canBeBoughtOnline: boolean;
  tfaCode?: string;
  warrantyType?: WarrantyType;
  owner: {
    dateOfBirth?: string;
    relation?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  usedByYoungerDriver?: boolean;
  isCarTrimmed: boolean | null;
  bonusPreOwnership: string;
  bonusLevelCode: string;
  mortgagee?: boolean;
  damageCountRecentYears: number;
  yearOfLastDamage?: number | null;
  yearlyKmNeed: number;
  configurationId?: number;
  trafikkforsikringsavgift?: Trafikkforsikringsavgift;
  rentalType?: AutoRentalTypeVariable | null;
};

export function mapToCommerceCar(data: {
  [key: string]: any;
}): CarProductBackend {
  const user = data.customerData.newCustomerResponse;
  const intro = data.formData[CAR_FIND_STEP.id] as CarProductIntroFormValues;
  const details = data.formData[
    CAR_ABOUT_STEP.id
  ] as CarProductDetailsFormValues;
  const price = data.formData[CAR_PRICE_STEP.id] as CarPriceFormValues;

  const isEmployee = isSTBEmployee(data.customerData);

  let owner: CarProductBackend["owner"] = {
    relation: price.carOwner !== CarOwner.MYSELF ? price.carOwner : undefined,
  };

  if (
    price.carOwner === CarOwner.GUARDIAN ||
    price.carOwner === CarOwner.PARTNER
  ) {
    owner = {
      ...owner,
      dateOfBirth: getDateStringFromAge(Number(price.carOwnerAge)),
    };
  } else if (price.carOwner === CarOwner.MYSELF) {
    owner = {
      ...owner,
      dateOfBirth: user.dateOfBirth,
      firstName: user.providedPersonInfo?.name?.firstName,
      lastName: user.providedPersonInfo?.name?.lastName,
    };
  } else if (price.carOwner === CarOwner.LEASING) {
    owner = {
      ...owner,
      dateOfBirth: user.dateOfBirth,
    };
  }

  return {
    name: INSURANCE_NAME_VALUE.CAR,
    coverageGroup: data.coverage,

    type: intro.modelTypeName,
    trafikkforsikringsavgift: {
      monthlyAmount: price.tfaAmount.monthly,
      yearlyAmount: price.tfaAmount.yearly,
      code: intro.tfaCode,
    },

    // intro
    brand: intro.brand,
    model: intro.model,
    modelYear: Number(intro.year),
    licensePlateNumber: intro.licensePlateNumber || null,
    licensePlateNumberProvided:
      isDefined(intro.licensePlateNumber) &&
      intro.licensePlateNumber.length > 0,
    configurationId: intro.configurationId,
    kw: intro.kw,
    volume: intro.volume,
    canBeBoughtOnline: intro.canBeBoughtOnline,
    tfaCode: intro.tfaCode,
    warrantyType: intro.warrantyType,

    // details
    bonusPreOwnership: details.bonusPreOwnership,
    bonusLevelCode: details.bonus,

    mortgagee:
      price.carOwner !== CarOwner.LEASING
        ? formBooleanToBoolean(details.mortgagee)
        : false,
    ...mapFormToInjuries(details.injuries, details.injuryYear),

    // price
    yearlyKmNeed: isEmployee ? 1 : Number(price.yearlyKmNeed),
    usedByYoungerDriver: usedByYoungerDriver(
      data.customerData,
      price.usedByYoungerDriver,
      price.carOwnerAge,
    ),
    insuranceHolderIsOwner: price.carOwner === CarOwner.MYSELF,
    owner,
    isCarTrimmed:
      price.isCarTrimmed == BooleanFormType.FALSE
        ? formBooleanToBoolean(price.isCarTrimmed)
        : null,
    rentalType:
      price?.rentalType === RENTAL_TYPE_VARIABLE.NONE
        ? null
        : price?.rentalType,
  };
}
